import { useState, useMemo, useEffect } from 'react';
import { sliderTokens } from './slider.styles.js';

const BASE_OFFSET = 50;

/**
 * amount of extra pages to render in both directions to ensure that the user
 * never will be able to see jumps when quickly scrolling in a given direction.
 */
const EXTRA_ITEMS = 5;
/**
 * Handles infinitly sliding the slider in both directions by rendering
 * wrap-around clones of the original items such that it appears as if
 * the list of items is infinite.
 *
 * In practice the transform used to scroll in both directions may grow
 * infinitely to the right and only ${BASE_OFFSET} times to the left. As such
 * BASE_OFFSET should be so large that a user never will attempt to navigate
 * backwards that many times.
 * The reason for this is that translating into negative values comes with a
 * lot of headaches that can be avoided with this approach.
 */
function useInfiniteScrolling(sliderRef, pagination, originalItems) {
  const [lastStableAt, setLastStableAt] = useState(0);
  const startIndex = Math.min(lastStableAt, pagination.activePageIndex) * pagination.itemsPerPage;
  const endIndex = Math.ceil(Math.max(startIndex + originalItems.length - 1, Math.max(lastStableAt, pagination.activePageIndex) * pagination.itemsPerPage + originalItems.length));
  const items = useMemo(() => getItems(originalItems, Math.ceil(startIndex - EXTRA_ITEMS), Math.ceil(endIndex + EXTRA_ITEMS)).map((item, index) => ({
    item: {
      ...item,
      key: String(startIndex + index),
      index: index - EXTRA_ITEMS
    },
    // items should be disabled as longs as they're not part of the
    // currently original list of items.
    disabled: index < EXTRA_ITEMS || index > originalItems.length + EXTRA_ITEMS
  })), [endIndex, originalItems, startIndex]);
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) {
      return;
    }
    const onTransitionEnd = evt => {
      if (evt.target !== slider) {
        return;
      }
      setLastStableAt(pagination.activePageIndex);
    };
    slider.addEventListener("transitionend", onTransitionEnd);
    return () => {
      slider.removeEventListener("transitionend", onTransitionEnd);
    };
  }, [sliderRef, pagination.activePageIndex]);
  if (originalItems.length < pagination.itemsPerPage) {
    return {
      disabled: true,
      spacingBefore: 0,
      items: originalItems.map((item, index) => ({
        item: {
          ...item,
          key: String(index),
          index
        },
        disabled: false
      }))
    };
  }
  return {
    disabled: false,
    spacingBefore: Math.min(lastStableAt, pagination.activePageIndex) + BASE_OFFSET,
    items
  };
}

/**
 * Returns the transform: translate() style that is required for the slider to
 * be focused on the specified pageIndex
 */
function getTransformation(pagination, infinite) {
  const paginationOffset = pagination.type === "items" ? `(${pagination.activeItemIndex} / ${sliderTokens.itemsPerPage()})` : pagination.activePageIndex;
  const offset = `(${paginationOffset} + ${infinite ? BASE_OFFSET : 0} + (${infinite ? EXTRA_ITEMS : 0} / ${sliderTokens.itemsPerPage()}))`;
  const centerOffset = infinite ? "0%" : `calc(50% + ${sliderTokens.itemMargin()} * 0.5) - ((50% + 0.5 * ${sliderTokens.itemMargin()}) / ${sliderTokens.itemsPerPage()})`;
  return `translateX(calc(${offset} * -100% + ${centerOffset} + -1 * (${offset} + ${infinite ? 1 : 0}) * ${sliderTokens.itemMargin()}))`;
}

/**
 * returns an extended array of length toIndex - fromIndex which takes items
 * from the original array modulo its length.
 *
 * This is used to ensure that we render enough items in both 'ends' of the
 * infinite slider.
 */
function getItems(arr, fromIndex, toIndex) {
  const newArray = [];
  for (let i = fromIndex; i < toIndex; i++) {
    let index = i % arr.length;
    if (index < 0) {
      index = arr.length + index;
    }
    const item = arr[Number(index)];
    if (item) {
      newArray.push(item);
    }
  }
  return newArray;
}

export { BASE_OFFSET, EXTRA_ITEMS, getTransformation, useInfiniteScrolling };
//# sourceMappingURL=hook.infinite.js.map
